import { recordPageView, recordClick } from "@workspace/api-client-react";
import type { ClickInputTargetType } from "@workspace/api-client-react";

const VISITOR_KEY = "luma_visitor_id";
const SESSION_KEY = "luma_session_id";
const SESSION_TS_KEY = "luma_session_ts";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const CONSENT_KEY = "luma_analytics_consent";

export type ConsentChoice = "granted" | "denied";

/** Read the visitor's stored analytics-consent choice, if any. */
export function getConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/** Persist the visitor's analytics-consent choice. */
export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    /* storage unavailable; treat as no consent */
  }
}

/** Analytics only runs once the visitor has explicitly granted consent (opt-in). */
function hasConsent(): boolean {
  return getConsent() === "granted";
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return randomId();
  }
}

function getSessionId(): string {
  try {
    const now = Date.now();
    const lastTs = Number(sessionStorage.getItem(SESSION_TS_KEY) ?? 0);
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id || now - lastTs > SESSION_TIMEOUT_MS) {
      id = randomId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return id;
  } catch {
    return randomId();
  }
}

function externalReferrer(): string | null {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    const refHost = new URL(ref).hostname;
    if (refHost === window.location.hostname) return null;
    return refHost;
  } catch {
    return null;
  }
}

type Utm = {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
};

function utmParams(): Utm {
  const empty: Utm = {
    utmSource: null,
    utmMedium: null,
    utmCampaign: null,
    utmTerm: null,
    utmContent: null,
  };
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get("utm_source"),
      utmMedium: params.get("utm_medium"),
      utmCampaign: params.get("utm_campaign"),
      utmTerm: params.get("utm_term"),
      utmContent: params.get("utm_content"),
    };
  } catch {
    return empty;
  }
}

/**
 * Record an anonymous page view. Excludes admin routes. Fire-and-forget.
 */
export function trackPageView(path: string): void {
  if (path.startsWith("/admin")) return;
  if (!hasConsent()) return;
  const { utmSource, utmMedium, utmCampaign, utmTerm, utmContent } = utmParams();
  void recordPageView({
    path,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    referrer: externalReferrer(),
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
  }).catch(() => {
    /* analytics is best-effort; never block UX */
  });
}

/**
 * Record an affiliate/partner outbound link click. Fire-and-forget.
 */
export function trackClick(args: {
  targetType: ClickInputTargetType;
  targetSlug: string;
  targetName: string;
  pagePath: string;
  platform?: string;
}): void {
  if (!hasConsent()) return;
  void recordClick({
    targetType: args.targetType,
    targetSlug: args.targetSlug,
    targetName: args.targetName,
    pagePath: args.pagePath,
    platform: args.platform ?? null,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
  }).catch(() => {
    /* analytics is best-effort; never block UX */
  });
}
